"use client";

import { Box, Typography } from "@mui/material";
import {
  DefaultValues,
  FieldValues,
  FormProvider,
  Path,
  SetValueConfig,
  useForm,
  UseFormWatch,
} from "react-hook-form";
import { forwardRef, useImperativeHandle, useEffect, useRef } from "react";
import { yupResolver } from "@hookform/resolvers/yup";

const Form = forwardRef(
  <T extends FieldValues>(
    {
      children,
      defaultValues,
      schema,
      isError = false,
      error = "",
      onSubmit,
    }: FormProps<T>,
    ref: React.Ref<FormRef>,
  ) => {
    const initializedRef = useRef(false);

    const methods = useForm<T>({
      resolver: yupResolver(schema) as any,
      defaultValues: defaultValues as DefaultValues<T>,
      mode: "onChange",
      reValidateMode: "onChange",
      shouldFocusError: true,
    });

    useEffect(() => {
      const hasData =
        defaultValues &&
        Object.keys(defaultValues).length > 0 &&
        Object.values(defaultValues).some(
          (value) => value !== undefined && value !== null && value !== "",
        );

      if (hasData && !initializedRef.current) {
        initializedRef.current = true;
        methods.reset(defaultValues as DefaultValues<T>);
      }
    }, [defaultValues, methods]);

    useImperativeHandle(ref, () => ({
      getFormState: () => methods.formState,
      getValues: () => methods.getValues(),
      reset: (values?: T, options?: any) => methods.reset(values, options),
      setValue: (name: Path<T>, value: any, options?: SetValueConfig) =>
        methods.setValue(name, value, options),
      setError: (name: Path<T>, message: string) =>
        methods.setError(name, { type: "server", message }),
      trigger: (name?: any) => methods.trigger(name),
      validateForm: async () => {
        return await methods.trigger();
      },
      watch: methods.watch as UseFormWatch<T>,
    }));

    const handleSubmit = methods.handleSubmit(
      (data: T) => {
        if (typeof onSubmit === "function") {
          return onSubmit(data);
        }
      },
      (errors) => {
        const firstError = Object.keys(errors)[0];
        if (firstError) {
          const errorElement = document.getElementsByName(firstError)[0];
          if (errorElement) {
            errorElement.focus();
            errorElement.scrollIntoView({
              behavior: "smooth",
              block: "center",
            });
          }
        }
      },
    );

    return (
      <FormProvider {...methods}>
        <Box component="form" onSubmit={handleSubmit}>
          {children}
          {isError && (
            <Typography color="error" sx={{ mt: 1 }}>
              Failed to save: {error.message}
            </Typography>
          )}
        </Box>
      </FormProvider>
    );
  },
);

Form.displayName = "Form";

export default Form;
